import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, X, Zap } from "lucide-react";
import { useEffect, useId, useRef } from "react";
import { useNavigate } from "react-router";

import { Button, cx } from "@/components/ui";
import { portal } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import type { Plan } from "@/lib/types";

/** "£0.00" rather than "Free": the two columns compare prices. */
const amount = (cents: number, currency?: string): string => formatMoney(cents, currency);

const FREE_FEATURES: [boolean, string][] = [
  [true, "1 published site on a free subdomain"],
  [false, "Build pages with Weavex AI"],
  [false, "Website templates"],
  [false, "Custom domain"],
];
const PAID_FEATURES = [
  "Build pages with Weavex AI",
  "All website templates",
  "Connect a custom domain",
  "Growth Intelligence for supported sectors",
];

/**
 * Figma "Upgrade required modal": shown when the plan doesn't cover what the user asked for
 * (building with Weavex AI, starting from a template). The recommended plan is the cheapest paid
 * one in the catalogue.
 */
export function UpgradeModal({
  open,
  onClose,
  current,
}: {
  open: boolean;
  onClose: () => void;
  current?: Plan;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const navigate = useNavigate();
  const plans = useQuery({
    queryKey: ["plans"],
    queryFn: portal.plans,
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const recommended = plans.data?.plans
    .filter((p) => p.monthly_price_cents > 0)
    .sort((a, b) => a.monthly_price_cents - b.monthly_price_cents)[0];

  useEffect(() => {
    const d = ref.current;
    if (open && d && !d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    }
  }, [open]);
  if (!open) return null;

  const name = recommended?.name ?? "Growth";
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      aria-labelledby={titleId}
      className="m-auto w-[calc(100%-2rem)] max-w-[600px] rounded-[20px] bg-surface p-0 text-fg shadow-[0_16px_48px_rgba(26,15,10,0.28)] backdrop:bg-[rgba(34,28,25,0.28)] backdrop:backdrop-blur-[8px]"
    >
      <div className="flex flex-col gap-5 px-7 pt-7 pb-6">
        <div className="flex items-center gap-3">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-[14px] border border-brand-100 bg-[#fef3f0] text-brand-500">
            <Zap className="size-[22px]" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 id={titleId} className="text-[22px] font-bold">
              Upgrade to build with Weavex AI
            </h2>
            <p className="text-sm leading-relaxed text-fg-muted">
              AI page generation, templates and more are in the paid plan.
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <PlanCard
            name={current?.name ?? "Free"}
            tag="Current plan"
            price={amount(current?.monthly_price_cents ?? 0, current?.currency ?? recommended?.currency)}
            note="Your plan today"
          >
            {FREE_FEATURES.map(([included, label]) => (
              <Feature key={label} included={included}>
                {label}
              </Feature>
            ))}
          </PlanCard>
          <PlanCard
            name={name}
            tag="Recommended"
            highlighted
            price={
              recommended ? `${amount(recommended.monthly_price_cents, recommended.currency)} / month` : "—"
            }
            note="+ VAT · full price shown before you pay"
          >
            {PAID_FEATURES.map((label) => (
              <Feature key={label} included>
                {label}
              </Feature>
            ))}
          </PlanCard>
        </div>

        <div className="flex flex-wrap justify-end gap-2.5">
          <Button
            unstyled
            type="button"
            onClick={onClose}
            className="rounded-full border border-border bg-surface px-5 py-[11px] text-sm font-semibold hover:bg-muted"
          >
            Maybe later
          </Button>
          <Button
            unstyled
            type="button"
            onClick={() => {
              onClose();
              navigate("/billing");
            }}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-500 px-5 py-[11px] text-sm font-semibold text-white shadow-[0_3px_10px_-2px_rgba(217,64,26,0.25)] hover:bg-brand-600"
          >
            Upgrade to {name} <ArrowRight className="size-3.5" aria-hidden />
          </Button>
        </div>
      </div>
    </dialog>
  );
}

function PlanCard({
  name,
  tag,
  price,
  note,
  highlighted = false,
  children,
}: {
  name: string;
  tag: string;
  price: string;
  note: string;
  highlighted?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cx(
        "flex flex-col gap-3 rounded-[14px] p-4",
        highlighted ? "border-[1.5px] border-brand-500 bg-[#fef3f0]" : "border border-border bg-brand-50",
      )}
    >
      <div className="flex items-center gap-2">
        <p className="text-[15px] font-bold">{name}</p>
        <span
          className={cx(
            "ml-auto rounded-full px-2 py-[3px] text-[10.5px] font-semibold tracking-[0.05em] uppercase",
            highlighted ? "bg-brand-500 text-white" : "bg-muted text-fg-muted",
          )}
        >
          {tag}
        </span>
      </div>
      <div>
        <p className="text-lg font-bold">{price}</p>
        <p className="text-xs text-fg-subtle">{note}</p>
      </div>
      <div className="h-px bg-border" />
      <ul className="flex flex-col gap-3">{children}</ul>
    </div>
  );
}

function Feature({ included, children }: { included: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-[13px] leading-normal">
      {included ? (
        <Check className="mt-0.5 size-[13px] shrink-0 text-success-fg" aria-label="Included" />
      ) : (
        <X className="mt-0.5 size-[13px] shrink-0 text-fg-subtle" aria-label="Not included" />
      )}
      <span className={included ? "text-fg" : "text-fg-subtle"}>{children}</span>
    </li>
  );
}
